"use client";

import * as X from "@/ui";
import ReactMarkdown from "react-markdown";
import { ArticleCover, AuthorAvatar, ArticleList, type Article, calculateReadingTime } from ".";

import "../styles/BlogArticle.scss";

export function ArticlePage(props: X.PageProps<{
  article: Article;
  similarArticles: Article[];
  readingTime: number;
  similarArticleTitle: string;
}>) {
  // const tr = X.useTranslator();
  const { article } = props;

  const [date, setDate] = X.useState(new Date(article.date).toLocaleDateString());
  X.useMountEffect(() => {
    const formatter = new Intl.DateTimeFormat(props.locale, {
      dateStyle: "medium",
    });

    setDate(formatter.format(new Date(article.date)));
  });

  return (
    <X.MainLayout
      locale={props.locale}
      altHrefs={props.altHrefs}
    >
      <div className={X.clsx(
        "xl:max-w-5xl xl:mx-auto",
      )}>
        <div className="mx-mobile-margin">
          <div className="text-[10px] xl:text-[12px] mb-4 text-end">Blog › {article.category.name}</div>
          <div className="text-[14px] xl:text-[18px] mb-2">{article.category.name}</div>
          <h1 className="text-[24px] font-medium xl:font-normal xl:text-[54px] leading-[28px] xl:leading-[64px]">
            <ReactMarkdown components={{
              p: ({ ...props }) => <>{props.children}</>,
              h1: ({ ...props }) => <>{props.children}</>,
              h2: ({ ...props }) => <>{props.children}</>,
              h3: ({ ...props }) => <>{props.children}</>,
              h4: ({ ...props }) => <>{props.children}</>,
            }}>
              {article.titleH1}
            </ReactMarkdown>

          </h1>

          <div className="flex flex-row justify-between items-center mt-8 xl:mt-12 text-[10px] xl:text-[12px] xl:px-4">
            <div className="flex flex-row items-center gap-2">
              {article.author?.avatar && (
                <div className="w-[24px] h-[24px] xl:w-[32px] xl:h-[32px] rounded-full">
                  <AuthorAvatar author={article.author} size="small" className="rounded-full" />
                </div>
              )}
              <p>{article.author?.fullname}</p>
            </div>
            <div className="flex flex-row items-center gap-2">
              <X.Icon.Clock size={12} />
              <p>
                {/* {props.readingTime} */}  {calculateReadingTime(props.article)} min. - {date}
              </p>
            </div>
          </div>
        </div>

        <div className="mx-mobile-margin-half xl:mx-0">
          <ArticleCover
            article={article}
            className="w-full h-auto my-4 xl:my-8 rounded-xl xl:rounded-2xl"
            priority
          />
        </div>

        <div className={X.clsx(
          "BlogArticleContent my-8 xl:mb-4 xl:py-4 px-mobile-margin xl:px-8",
          "leading-tight xl:leading-normal text-[16px] xl:text-[18px]",
        )}>
          <ReactMarkdown components={{
            // prevent double h1 on the page -> shift all headers to +1 level
            h1: ({ ...props }) => <h2 {...props} />,
            h2: ({ ...props }) => <h3 {...props} />,
            h3: ({ ...props }) => <h4 {...props} />,
            h4: ({ ...props }) => <h5 {...props} />,
            h5: ({ ...props }) => <h6 {...props} />,
            h6: ({ ...props }) => <p {...props} />,
          }}>
            {article.content}
          </ReactMarkdown>
        </div>
      </div>

      <div className="mb-8 xl:mb-0">
        <ArticleList
          locale={props.locale}
          articles={props.similarArticles}
          title={props.similarArticleTitle}
          moreArticleCta={""}
          moreArticleCtaLoadingState={""}        // title={tr({
        //   en: "On the same topic",
        //   fr: "Sur le même sujet",
        // })}
        />
      </div>

      <X.Newsletter locale={props.locale} />
    </X.MainLayout>
  );
}
